import _                             from 'underscore';
import { useEffect }                 from 'react';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { useAuth0 }                  from '@auth0/auth0-react';
import { v5 as uuidv5 }              from "uuid";
import { LDUser }                    from 'launchdarkly-js-client-sdk';

import { useLogging }                                                                                                  from './useLogging';
import { useWindowExit }                                                                                               from './useWindowExit';
import { CorvalAccount, CorvalOrganizationAccount, CorvalOrganizationRole, CorvalProjectRole, CorvalSystemRole, Uuid } from '../types';
import { promisedTimeout, RouterUtility, runIfFunction }                                                               from '../util';
import { UserQueries }                                                                                                 from '../queries';
import { RoleAccessService }                                                                                           from '../services';
import { queriesHaveData, queriesHaveErrors, queriesLoading }                                                          from '../store/_apolloUtil';
import { UserStore }                                                                                                   from '../store/UserStore';
import { CONSTANTS }                                                                                                   from '../constants';
import { ORGANIZATIONAL_ROLES, PROJECT_ROLES, SYSTEM_ROLES }                                                           from '../constants/ProductEnums';
import { RolePolicyType, RoleType }                                                                                    from '../services/RoleAccessService';
import { getAuthZeroClientId }                                                                                         from '../views/components/util/external/AuthZeroProvider';
import { ENVIRONMENT_LABEL, PRODUCT_VERSION }                                                                          from '../constants/SystemEnums';
import { EnvironmentObject }                                                                                           from '../environments/types/base';


export type CorvalMeAccount = Pick<CorvalAccount, 'id' | 'firstName' | 'lastName' | 'emailAddress' | 'systemRole' | 'organizations' | 'projects' | 'createdUtc'>;

export interface CorvalLDUser extends LDUser {
    custom?: {
        [Attribute in keyof LDIdentificationCustomFields]: LDIdentificationCustomFields[Attribute]
    }
}

export interface LDIdentificationFields extends CorvalLDUser {
    key?: Uuid,
    anonymous?: boolean,
}

export interface LDIdentificationCustomFields extends LDUserBasedIdentificationCustomFields, LDProjectBasedIdentificationCustomFields {
    tenant: string,
    product_version: string,
    product_environment: EnvironmentObject['envTag']
}

export type LDUserBasedIdentificationCustomFields = {
    firstName?: string,
    lastName?: string,
    email?: string,
    user_id?: Uuid,
    systemRole?: SYSTEM_ROLES,
}

export type LDProjectBasedIdentificationCustomFields = {
    projectId?: Uuid,
    projectRole?: PROJECT_ROLES,
    organizationId?: Uuid,
    organizationRole?: ORGANIZATIONAL_ROLES,
}

export const useCurrentUser = ( queryOptions = {} ) => {

    // Get the Auth state from Auth0
    let {
            user:            AuthZeroUser,            // user w/ scoped details from Auth0
            isAuthenticated: isAuthZeroAuthenticated, // whether Auth0 authenticated the user or not
            isLoading:       isAuthZeroLoading        // whether Auth0 is loading or not
        } = useAuth0();

    // Get the user, cache-first unless fetchPolicy is stipulated in queryOptions
    let currentUserReq = useQuery<{ me: CorvalMeAccount }>( UserQueries.queries.GET_CURRENT_USER(), {
        skip:        !isAuthZeroAuthenticated,
        errorPolicy: 'ignore',
        ...queryOptions,
    } );

    // When current user changes...
    useEffect( () => {}, [ currentUserReq ] )

    // When Auth0 changes...
    useEffect( () => {

        // console.log( authZeroContext );

    }, [ AuthZeroUser, isAuthZeroAuthenticated, isAuthZeroLoading ] )

    const getCurrentUser = (): CorvalMeAccount | null => {

        let currentUserData = ( queriesHaveData( currentUserReq ) && !queriesHaveErrors( currentUserReq ) )
                              ? currentUserReq.data.me
                              : null;

        if( !isAuthZeroLoading && isAuthZeroAuthenticated && currentUserData ){

            let activeOrgs = _.chain( currentUserData.organizations )
                              .filter( o => o.isActive )
                              .pluck( 'organizationId' )
                              .value();

            return {
                ...currentUserData,
                organizations: _.filter( currentUserData.organizations, { isActive: true } ),
                projects:      _.filter( currentUserData.projects, p => _.contains( activeOrgs, p.project.organizationId ) )
            }
        }

        return null;
    };

    const getLDUserIdentification = ( customFields: LDProjectBasedIdentificationCustomFields = {} ): LDIdentificationFields => {

        let currentUser = getCurrentUser();

        return ( currentUser ) ? {

            // namespaced key using domain and user id for unique keys across same accounts but different tenants / domains
            key:       uuidv5( window.location.hostname, currentUser.id ),
            firstName: currentUser.firstName,
            lastName:  currentUser.lastName,
            email:     currentUser.emailAddress,
            custom:    {

                // used for segmentation in LD
                tenant: window.location.hostname,

                // actual user id in custom data to key on if needed in segmentation rules
                user_id: currentUser.id,

                systemRole:          currentUser.systemRole as unknown as SYSTEM_ROLES,
                product_version:     PRODUCT_VERSION,
                product_environment: ENVIRONMENT_LABEL as EnvironmentObject['envTag'],

                ...customFields
            },

        } : getLDAnonymousIdentification() as LDIdentificationFields
    }

    const getLDAnonymousIdentification = (): LDIdentificationFields => {

        return {

            anonymous: true,

            custom: {
                tenant:              window.location.hostname,
                product_version:     PRODUCT_VERSION,
                product_environment: ENVIRONMENT_LABEL,
            },
        }
    }

    const hasSystemRole = ( roleType: SYSTEM_ROLES = null ) => {

        let currentUser = getCurrentUser();

        return ( currentUser ) ? ( ( roleType as unknown as CorvalSystemRole ) === currentUser.systemRole ) : false;
    };

    const isSystemAdmin = () => {

        return hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.CORVALADMIN )
               || hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.DECISIONENGINEADMIN )
               || hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.ACCOUNTSADMIN );
    };

    const isSystemCorvalAdmin = () => {

        return hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.CORVALADMIN );
    };

    const isSystemDEMAdmin = () => {

        return hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.DECISIONENGINEADMIN );
    };

    const isSystemAccountsAdmin = () => {

        return hasSystemRole( CONSTANTS.PRODUCT.SYSTEM_ROLES.ACCOUNTSADMIN );
    };

    const hasOrganizationalRole = ( organizationId: Uuid = null, roleType: ORGANIZATIONAL_ROLES = null ) => {

        let currentUser = getCurrentUser(),
            organization;

        if( currentUser ){

            organization = _.findWhere( currentUser.organizations, { organizationId } );

            return organization ? ( roleType as unknown as CorvalOrganizationRole === organization.organizationRole ) : false;
        }

        return false;
    };

    const hasRoleInOrganizations = ( roleType: ORGANIZATIONAL_ROLES = null ) => {

        let currentUser = getCurrentUser();

        if( currentUser ){

            const orgCheck = ( orgAccount: CorvalOrganizationAccount ) => hasOrganizationalRole( orgAccount?.organizationId, roleType )
            return ( {
                hasRole: !_.chain( currentUser.organizations ).map( orgCheck ).compact().isEmpty().value(),
                orgs:    _.chain( currentUser.organizations ).filter( orgCheck ).value()
            } )
        }

        return {
            hasRole: false,
            orgs:    [],
        };
    };

    const hasProjectRole = ( projectId: Uuid = null, roleType: PROJECT_ROLES = null ) => {

        let currentUser = getCurrentUser(),
            project;

        if( currentUser ){

            project = _.findWhere( currentUser.projects, { projectId } );

            return project ? ( roleType as unknown as CorvalProjectRole === project.projectRole ) : false;
        }
    };

    const canCurrentUser = ( policies: RolePolicyType | RolePolicyType[] = [], inclusive = true, roleType: RoleType = null ) => {

        // eg: useAccount.canCurrentUser( CONSTANTS.ROLE_POLICIES.DELETE_STAFF );
        let currentUser = getCurrentUser();

        if( currentUser && _.isString( roleType ) ){

            if( _.isString( policies ) ){
                return RoleAccessService.roleHasPolicyAccess( roleType, policies as RolePolicyType );
            }

            if( _.isArray( policies ) && policies.length > 0 ){

                return ( inclusive ) ?
                       !_.contains( policies.map( key => RoleAccessService.roleHasPolicyAccess( roleType, key ) ), false ) :  // AND
                       _.contains( policies.map( key => RoleAccessService.roleHasPolicyAccess( roleType, key ) ), true );    // OR
            }
        }

        return false;
    };

    const hasSystemRoleAccessTo = ( policies: RolePolicyType | RolePolicyType[] = [], inclusive = true ) => {

        let currentUser = getCurrentUser();

        if( currentUser ){

            return canCurrentUser( policies, inclusive, currentUser.systemRole as unknown as SYSTEM_ROLES )
        }

        return false;
    };

    const hasOrganizationalRoleAccessTo = ( organizationId: Uuid = null, policies: RolePolicyType | RolePolicyType[] = [], inclusive = true ) => {

        let currentUser = getCurrentUser(),
            organization;

        if( currentUser ){

            organization = _.findWhere( currentUser.organizations, { organizationId } );

            return organization ? canCurrentUser( policies, inclusive, organization.organizationRole as unknown as ORGANIZATIONAL_ROLES ) : false;
        }

        return false;
    };

    const hasProjectRoleAccessTo = ( projectId: Uuid = null, policies: RolePolicyType | RolePolicyType[] = [], inclusive = true ) => {

        let currentUser = getCurrentUser(),
            project;

        if( currentUser ){

            project = _.findWhere( currentUser.projects, { projectId } );

            return project ? canCurrentUser( policies, inclusive, project.projectRole as unknown as PROJECT_ROLES ) : false;
        }

        return false;
    };

    return {
        currentUser:          getCurrentUser(),
        currentUserReq:       currentUserReq,
        currentUserIsLoading: queriesLoading( currentUserReq ),
        getLDUserIdentification,
        getLDAnonymousIdentification,
        isSystemAdmin,
        isSystemCorvalAdmin,
        isSystemDEMAdmin,
        isSystemAccountsAdmin,
        canCurrentUser,
        hasSystemRole,
        hasSystemRoleAccessTo,
        hasOrganizationalRole,
        hasOrganizationalRoleAccessTo,
        hasRoleInOrganizations,
        hasProjectRole,
        hasProjectRoleAccessTo,
    }
};

interface LoginHook {

    getAuthenticatedCorvalUser(): Promise<CorvalAccount> | Promise<Error>,
}

export const useLogin = (): LoginHook => {

    let apolloClient        = useApolloClient(),
        { isAuthenticated } = useAuth0();

    return {

        getAuthenticatedCorvalUser: async () => {

            if( isAuthenticated ){

                try{

                    return apolloClient
                        .query( {

                            query: UserQueries.queries.GET_CURRENT_USER()
                        } )
                        .then( ( { data } ) => {

                            // @ts-ignore
                            return Promise.resolve( data.me );

                        } );

                } catch( e ) {

                    return Promise.reject( e );
                }
            }

            return Promise.reject( null );
        }
    }
};

export const useLogout = () => {

    let apolloClient                  = useApolloClient(),
        { clearSessionLog }           = useLogging(),
        { removeWindowExitListeners } = useWindowExit(),
        { logout }                    = useAuth0();

    return {

        logout: async ( onLogout: () => void = null ) => {

            // unregister window listeners
            removeWindowExitListeners();

            try{

                // preapare redirect path
                let currentUrl         = window.location.pathname,
                    blacklistRedirects = [
                        RouterUtility.getRoute( 'LOGIN' ),
                        RouterUtility.getRoute( 'LOGOUT' ),
                        RouterUtility.getRoute( 'NOT_FOUND' ),
                        RouterUtility.getRoute( 'NO_ACCOUNT' ),
                    ];

                let redirectUrl = !blacklistRedirects.includes( currentUrl ) ? currentUrl : null;

                // Clear Auth0 session, logging out of the client app we care about...
                // strips the Auth0 Provider of information it knows about the user,
                // and returns to the login page
                logout( {
                    client_id: getAuthZeroClientId(),
                    returnTo:  `${ window.location.origin }${ RouterUtility.getLoginURL( redirectUrl ) }`
                } );

                console.log( `[SESSION]: User removed from session.` );

                // wait a sec.
                await promisedTimeout( CONSTANTS.UI.DISPLAY.TRANSITION_DELAY );

                // Run any cleanup functionality afterwards
                runIfFunction( onLogout );

            } catch( e ) {

                // clean up items on client side.
                UserStore.logoutUser( null, null, { client: apolloClient } );
            }

            // clean up user's session
            clearSessionLog();

            return Promise.resolve( true );
        }
    }
};
